import React, { useState, useEffect, useRef } from 'react';
import { Send, Clock, ChevronDown, RefreshCw } from 'lucide-react';

const PHONE = '5516991378312';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const isBusinessHours = () => {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  if (day === 0) return false;
  if (day === 6) return hour >= 8 && hour < 12;
  return hour >= 8 && hour < 18;
};

// Gera um session_id único por visitante (persistente na sessão)
const getSessionId = () => {
  let sid = sessionStorage.getItem('ft_chat_session');
  if (!sid) {
    sid = 'ft-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
    sessionStorage.setItem('ft_chat_session', sid);
  }
  return sid;
};

// Fundo estilo chat (pontos sutis)
const chatBg = {
  backgroundColor: '#dfe7ed',
  backgroundImage: 'radial-gradient(circle, #b8c5cf 1px, transparent 1px)',
  backgroundSize: '18px 18px',
};

// ── Ícone Robô SVG ──────────────────────────────────────────────────────────
const RobotIcon = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="20" y1="9" x2="20" y2="4" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="20" cy="2.8" r="2.5" fill="#67e8f9" />
    <rect x="7" y="9" width="26" height="22" rx="7" fill="white" />
    <rect x="10" y="12" width="20" height="16" rx="4" fill="#0c4a6e" />
    <circle cx="15.5" cy="20" r="3.5" fill="#22d3ee" />
    <circle cx="15.5" cy="20" r="1.6" fill="white" />
    <circle cx="16.3" cy="19.3" r="0.7" fill="#0c4a6e" />
    <circle cx="24.5" cy="20" r="3.5" fill="#22d3ee" />
    <circle cx="24.5" cy="20" r="1.6" fill="white" />
    <circle cx="25.3" cy="19.3" r="0.7" fill="#0c4a6e" />
    <path d="M14.5 25.5 Q20 29 25.5 25.5" stroke="#22d3ee" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    <rect x="3" y="16" width="4.5" height="9" rx="2.5" fill="white" />
    <rect x="32.5" y="16" width="4.5" height="9" rx="2.5" fill="white" />
    <rect x="4.5" y="18" width="1.5" height="5" rx="0.75" fill="#22d3ee" />
    <rect x="34" y="18" width="1.5" height="5" rx="0.75" fill="#22d3ee" />
  </svg>
);

// ── Avatar do Robô para o Header ────────────────────────────────────────────
const RobotAvatar = ({ size = 40 }) => (
  <div
    className="flex items-center justify-center rounded-full shrink-0"
    style={{
      width: size, height: size,
      background: 'linear-gradient(135deg, #0c4a6e 0%, #0e7490 100%)',
      border: '2px solid rgba(34,211,238,0.4)',
      boxShadow: '0 0 12px rgba(34,211,238,0.3)',
    }}
  >
    <RobotIcon size={size * 0.7} />
  </div>
);

// ── Sugestões iniciais (ações rápidas) ──────────────────────────────────────
const QUICK_SUGGESTIONS = [
  'Quanto custa formatar meu PC?',
  'Troca de tela de notebook',
  'Orçamento de câmeras Intelbras',
  'Quero testar o SHOficina ERP',
];

// ── Componente de mensagem ──────────────────────────────────────────────────
const ChatBubble = ({ role, text }) => {
  const isBot = role === 'bot';
  return (
    <div className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`rounded-xl px-3 py-2.5 shadow-sm max-w-[88%] text-sm leading-relaxed whitespace-pre-wrap ${
          isBot ? 'bg-white rounded-tl-sm text-[#111b21]' : 'bg-[#d9fdd3] rounded-tr-sm text-[#111b21]'
        }`}
      >
        {text}
        {isBot && (
          <p className="text-right text-[10px] text-[#8696a0] mt-1.5 flex items-center justify-end gap-0.5">
            Agente FT
            <svg width="16" height="11" viewBox="0 0 16 11" fill="none" className="inline ml-0.5 text-[#4fc3f7]">
              <path d="M1 5.5L4.5 9L10 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M6 5.5L9.5 9L15 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </p>
        )}
      </div>
    </div>
  );
};

// ── Indicador "digitando..." ────────────────────────────────────────────────
const TypingDots = () => (
  <div className="flex justify-start">
    <div className="bg-white rounded-xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1">
      <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  </div>
);

// ── Componente principal ────────────────────────────────────────────────────
export default function WhatsAppChatWidget() {
  const [isOpen, setIsOpen]       = useState(false);
  const [message, setMessage]     = useState('');
  const [online, setOnline]       = useState(isBusinessHours());
  const [showPulse, setShowPulse] = useState(false);
  const [messages, setMessages]   = useState([]);
  const [sending, setSending]     = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);

  const inputRef  = useRef(null);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  // Mensagem de boas-vindas (injetada ao abrir pela 1ª vez)
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'bot',
        text: (online
          ? '👋 Olá! Sou o Agente Fernando Tech. Estou online e pronto para te ajudar!\n\nPergunte sobre formatação, câmeras, SHOficina ERP, troca de tela, suporte de TI e mais. 🚀'
          : '👋 Olá! Estamos fora do horário, mas você pode me perguntar sobre serviços e preços. Se precisar de atendimento humano, te conecto ao WhatsApp. 😊'
        ),
      }]);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('wachat_seen')) return;
    const t = setTimeout(() => setShowPulse(true), 5000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setOnline(isBusinessHours()), 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  // Auto-scroll para última mensagem
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const handleOpen = () => {
    setIsOpen(true);
    setShowPulse(false);
    sessionStorage.setItem('wachat_seen', '1');
  };

  const handleClose = () => setIsOpen(false);

  const handleReset = () => {
    sessionStorage.removeItem('ft_chat_session');
    sessionId.current = getSessionId();
    setMessages([{
      role: 'bot',
      text: '🔄 Conversa reiniciada! Como posso te ajudar agora?',
    }]);
    setShowHandoff(false);
  };

  const sendToApi = async (text) => {
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', text }]);

    try {
      const res = await fetch(`${API_BASE}/api/chatbot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'bot', text: data.reply }]);
      if (data.should_handoff_whatsapp) setShowHandoff(true);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'bot',
        text: '⚠️ Tive uma instabilidade aqui. Fale direto pelo WhatsApp: (16) 99137-8312',
      }]);
      setShowHandoff(true);
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    const text = message.trim();
    if (!text || sending) return;
    setMessage('');
    sendToApi(text);
  };

  const handleSuggestion = (sug) => {
    if (sending) return;
    sendToApi(sug);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const goToWhatsApp = () => {
    // Usa a última mensagem do usuário como contexto
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    const context = lastUser
      ? `Olá! Vim pelo site e conversei com o Agente IA sobre: "${lastUser.text}". Gostaria de falar com um atendente humano.`
      : 'Olá! Vim pelo site da Fernando Tech e gostaria de mais informações.';
    window.open(`https://wa.me/${PHONE}?text=${encodeURIComponent(context)}`, '_blank');
  };

  const showSuggestions = messages.length <= 1 && !sending;

  return (
    <>
      {/* ── Painel de Chat ───────────────────────────────────────────── */}
      <div
        className="fixed bottom-24 left-4 z-[199] w-[340px] max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{
          transition: 'opacity 0.25s ease, transform 0.25s ease',
          opacity:       isOpen ? 1 : 0,
          transform:     isOpen ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(12px)',
          pointerEvents: isOpen ? 'auto' : 'none',
          transformOrigin: 'bottom left',
          maxHeight: '80vh',
        }}
        data-testid="whatsapp-chat-panel"
      >
        {/* Header com tema azul/tech */}
        <div
          className="px-4 py-3 flex items-center justify-between shrink-0"
          style={{ background: 'linear-gradient(135deg, #0c4a6e 0%, #0e7490 60%, #0f766e 100%)' }}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <RobotAvatar size={42} />
              <span
                className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                  online
                    ? 'bg-green-400 border-[#0c4a6e]'
                    : 'bg-gray-400 border-[#0c4a6e]'
                }`}
              />
            </div>
            <div>
              <p className="text-white font-semibold text-sm leading-tight">Agente Fernando Tech</p>
              <p className={`text-xs leading-tight ${online ? 'text-cyan-300' : 'text-gray-400'}`}>
                {online ? '● Assistente IA · Online' : '● Assistente IA · Fora do horário'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleReset}
              className="text-white/60 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Reiniciar conversa"
              title="Reiniciar conversa"
              data-testid="whatsapp-chat-reset"
            >
              <RefreshCw size={15} />
            </button>
            <button
              onClick={handleClose}
              className="text-white/60 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Fechar chat"
              data-testid="whatsapp-chat-close"
            >
              <ChevronDown size={18} />
            </button>
          </div>
        </div>

        {/* Área de mensagens */}
        <div
          ref={scrollRef}
          className="px-4 py-4 space-y-2 overflow-y-auto flex-1"
          style={{ ...chatBg, minHeight: '280px', maxHeight: '420px' }}
          data-testid="whatsapp-chat-messages"
        >
          {messages.map((m, i) => (
            <ChatBubble key={i} role={m.role} text={m.text} />
          ))}
          {sending && <TypingDots />}

          {/* Sugestões rápidas (só no começo) */}
          {showSuggestions && (
            <div className="pt-2 space-y-1.5">
              <p className="text-[11px] text-slate-600 font-medium pl-1">Sugestões:</p>
              {QUICK_SUGGESTIONS.map(sug => (
                <button
                  key={sug}
                  onClick={() => handleSuggestion(sug)}
                  className="w-full text-left px-3 py-2 bg-white hover:bg-cyan-50 border border-cyan-200 rounded-xl text-xs text-cyan-800 font-medium transition-colors shadow-sm"
                  data-testid={`suggestion-${sug.slice(0, 12)}`}
                >
                  💬 {sug}
                </button>
              ))}
            </div>
          )}

          {/* Botão de transferir para WhatsApp (aparece quando IA sugere) */}
          {showHandoff && !sending && (
            <div className="pt-2 flex justify-center">
              <button
                onClick={goToWhatsApp}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-full shadow-md transition-all hover:scale-105"
                data-testid="whatsapp-chat-handoff"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L0 24l6.338-1.497A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.789 9.789 0 01-4.99-1.364l-.358-.213-3.76.888.937-3.647-.234-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z" /></svg>
                Falar com atendente humano
              </button>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="bg-[#f0f2f5] px-3 py-3 flex items-end gap-2 shrink-0">
          <textarea
            ref={inputRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={sending ? 'Aguarde a resposta...' : 'Pergunte algo...'}
            disabled={sending}
            rows={1}
            className="flex-1 resize-none bg-white rounded-xl px-3 py-2 text-sm text-[#111b21] placeholder-gray-400 outline-none focus:ring-1 focus:ring-cyan-400/60 shadow-sm border border-gray-100 disabled:opacity-60"
            data-testid="whatsapp-chat-input"
            style={{ maxHeight: '80px' }}
          />
          <button
            onClick={handleSend}
            disabled={sending || !message.trim()}
            className="text-white p-2.5 rounded-full shadow-md transition-all hover:scale-105 active:scale-95 shrink-0 mb-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ background: 'linear-gradient(135deg, #0e7490, #0891b2)' }}
            aria-label="Enviar mensagem"
            data-testid="whatsapp-chat-send"
          >
            <Send size={17} />
          </button>
        </div>

        {/* Aviso fora do horário */}
        {!online && (
          <div className="bg-amber-50 border-t border-amber-100 px-4 py-2 flex items-center gap-1.5 shrink-0">
            <Clock size={11} className="text-amber-600 shrink-0" />
            <p className="text-amber-700 text-[11px]">Horário humano: Seg–Sex 8h–18h · Sáb 8h–12h</p>
          </div>
        )}

        {/* Rodapé */}
        <div className="bg-[#f0f2f5] px-4 py-1.5 border-t border-gray-200 text-center shrink-0">
          <p className="text-[10px] text-gray-400">🤖 Respostas geradas por IA · Confira sempre com um atendente</p>
        </div>
      </div>

      {/* ── Botão FAB com Robô ───────────────────────────────────────── */}
      <button
        onClick={handleOpen}
        className="fixed bottom-4 left-4 z-[200] w-16 h-16 rounded-full flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, #0c4a6e 0%, #0e7490 50%, #0891b2 100%)',
          boxShadow: showPulse
            ? '0 0 0 0 rgba(34,211,238,0), 0 8px 30px rgba(14,116,144,0.6)'
            : '0 8px 30px rgba(14,116,144,0.5)',
          opacity:       isOpen ? 0 : 1,
          transform:     isOpen ? 'scale(0)' : 'scale(1)',
          pointerEvents: isOpen ? 'none' : 'auto',
          transition:    'opacity 0.2s ease, transform 0.2s ease, box-shadow 0.3s ease',
          border: '2px solid rgba(34,211,238,0.35)',
        }}
        aria-label="Abrir chat com o Agente Fernando Tech"
        data-testid="whatsapp-chat-fab"
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; e.currentTarget.style.boxShadow = '0 0 25px rgba(34,211,238,0.5), 0 8px 30px rgba(14,116,144,0.6)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(14,116,144,0.5)'; }}
      >
        <RobotIcon size={38} />

        {/* Anel pulsante + badge */}
        {showPulse && (
          <>
            <span
              className="absolute inset-0 rounded-full animate-ping"
              style={{ background: 'rgba(34,211,238,0.25)', animationDuration: '1.4s' }}
            />
            <span
              className="absolute inset-0 rounded-full animate-ping"
              style={{ background: 'rgba(34,211,238,0.12)', animationDuration: '1.4s', animationDelay: '0.7s' }}
            />
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-[9px] font-bold shadow-md z-10">
              1
            </span>
          </>
        )}

        {/* Label "Suporte" que aparece junto ao botão */}
        {!isOpen && showPulse && (
          <span
            className="absolute left-[72px] top-1/2 -translate-y-1/2 bg-slate-900 text-cyan-300 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap shadow-lg border border-cyan-800/50"
            style={{ animation: 'fadeSlideIn 0.4s ease' }}
          >
            Assistente IA
          </span>
        )}
      </button>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-50%) translateX(-8px); }
          to   { opacity: 1; transform: translateY(-50%) translateX(0); }
        }
      `}</style>
    </>
  );
}
